'use client';

import { c as _c } from "react/compiler-runtime";
import { ActiveWalletProvider, type ActiveWalletSlot, ArchivedWalletsProvider, type SdkCreateWalletParams, type SdkExportWalletParams, type SdkExportWalletResult, type SdkImportWalletParams, type SdkRenameWalletParams, type SdkWallet, type UseArchivedWalletsResult, type UseWalletsResult, useActiveWalletSlot, useArchivedWallets, useWallets, WalletsProvider } from '@morfi/backend-sdk-v2/client';
import { type Context, createContext, type ReactNode, useContext, useEffect, useRef } from 'react';
import { onEvent } from '@/events';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { useImpersonationStore } from '@/lib/impersonation/store';
import { logger } from '@/lib/telemetry/logger';
import { canStartMultiWalletOperation, deriveMultiWalletGateState, type MultiWalletGateState, type MultiWalletOperation, type MultiWalletSurface, resolveMultiWalletScope } from '@/lib/wallet/multi-wallet-scope';
import { useActiveWalletStore } from '@/stores/active-wallet';
interface MultiWalletFeatureValue {
  readonly state: MultiWalletGateState;
  readonly enabled: boolean;
  readonly primaryWallet: SdkWallet | null;
  readonly canStartOperation: (operation: MultiWalletOperation, surface: MultiWalletSurface) => boolean;
}
interface RawWalletAccessValue {
  readonly wallets: UseWalletsResult;
  readonly archivedWallets: UseArchivedWalletsResult;
  readonly activeWalletSlot: ActiveWalletSlot;
}
const MultiWalletFeatureContext: Context<MultiWalletFeatureValue | null> = createContext<MultiWalletFeatureValue | null>(null);
const RawWalletAccessContext: Context<RawWalletAccessValue | null> = createContext<RawWalletAccessValue | null>(null);
const MISSING_FEATURE_PROVIDER_ERROR = 'Multi-wallet feature context is unavailable';
const MISSING_RAW_ACCESS_PROVIDER_ERROR = 'Raw wallet access context is unavailable';
class MultiWalletOperationBlockedError extends Error {
  constructor(operation: MultiWalletOperation) {
    super(`Wallet operation is unavailable: ${operation}`);
    this.name = 'MultiWalletOperationBlockedError';
  }
}
export function useMultiWalletFeature() {
  const value = useContext(MultiWalletFeatureContext);
  if (value === null) {
    throw new Error(MISSING_FEATURE_PROVIDER_ERROR);
  }
  return value;
}
export function useRawWalletAccess() {
  const value = useContext(RawWalletAccessContext);
  if (value === null) {
    throw new Error(MISSING_RAW_ACCESS_PROVIDER_ERROR);
  }
  return value;
}
export function MultiWalletFeatureBypass(t0) {
  const $ = _c(9);
  const {
    children
  } = t0;
  const raw = useRawWalletAccess();
  let t1;
  if ($[0] !== children || $[1] !== raw.activeWalletSlot) {
    t1 = <ActiveWalletProvider value={raw.activeWalletSlot}>{children}</ActiveWalletProvider>;
    $[0] = children;
    $[1] = raw.activeWalletSlot;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== raw.archivedWallets || $[4] !== t1) {
    t2 = <ArchivedWalletsProvider value={raw.archivedWallets}>{t1}</ArchivedWalletsProvider>;
    $[3] = raw.archivedWallets;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== raw.wallets || $[7] !== t2) {
    t3 = <WalletsProvider value={raw.wallets}>{t2}</WalletsProvider>;
    $[6] = raw.wallets;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
export function MultiWalletFeatureProvider(t0) {
  const $ = _c(67);
  const {
    children
  } = t0;
  const gateState = deriveMultiWalletGateState(useFeatureGate("multi_wallets"));
  const rawWallets = useWallets(false);
  const rawArchivedWallets = useArchivedWallets();
  const rawActiveWalletSlot = useActiveWalletSlot();
  const activeStoreHydrated = useActiveWalletStore(_temp);
  const isImpersonating = useImpersonationStore(_temp2);
  const gateStateRef = useRef(gateState);
  const warningFingerprintRef = useRef(null);
  const scope = resolveMultiWalletScope({
    gateState,
    wallets: rawWallets.wallets,
    archivedWallets: rawArchivedWallets.archivedWallets,
    activeWalletId: rawActiveWalletSlot.activeWalletId
  });
  let t1;
  if ($[0] !== gateState || $[1] !== gateStateRef) {
    t1 = () => {
      gateStateRef.current = gateState;
    };
    $[0] = gateState;
    $[1] = gateStateRef;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== gateState) {
    t2 = [gateState];
    $[3] = gateState;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[5] !== activeStoreHydrated || $[6] !== gateState || $[7] !== isImpersonating || $[8] !== rawActiveWalletSlot || $[9] !== rawWallets.isLoading || $[10] !== scope.primaryWallet) {
    t3 = () => {
      if (gateState === "on" || isImpersonating || rawWallets.isLoading || !(rawActiveWalletSlot.isHydrated || activeStoreHydrated) || scope.primaryWallet === null || !scope.primaryWallet.isAvailable || rawActiveWalletSlot.activeWalletId === scope.primaryWallet.id) {
        return;
      }
      rawActiveWalletSlot.setActiveWalletId(scope.primaryWallet.id);
    };
    t4 = [activeStoreHydrated, gateState, isImpersonating, rawActiveWalletSlot, rawWallets.isLoading, scope.primaryWallet];
    $[5] = activeStoreHydrated;
    $[6] = gateState;
    $[7] = isImpersonating;
    $[8] = rawActiveWalletSlot;
    $[9] = rawWallets.isLoading;
    $[10] = scope.primaryWallet;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[13] !== gateState || $[14] !== rawWallets.isLoading || $[15] !== rawWallets.wallets) {
    t5 = () => {
      if (gateState === "on" || rawWallets.isLoading) {
        return;
      }
      if (rawWallets.wallets.length === 0) {
        return;
      }
      const embeddedPrimaries = rawWallets.wallets.filter(_temp3);
      const primaryCandidates = embeddedPrimaries.filter(_temp4);
      const reason = resolvePrimaryFailureReason(embeddedPrimaries, primaryCandidates);
      if (reason === null) {
        return;
      }
      const warning = {
        gate_state: gateState,
        wallets_loaded: true,
        wallet_count: rawWallets.wallets.length,
        primary_candidate_count: primaryCandidates.length,
        embedded_primary_count: embeddedPrimaries.length,
        reason
      } as const;
      const fingerprint = JSON.stringify(warning);
      if (warningFingerprintRef.current === fingerprint) {
        return;
      }
      warningFingerprintRef.current = fingerprint;
      logger.warn("wallet.primary_resolution_failed", warning);
    };
    t6 = [gateState, rawWallets.isLoading, rawWallets.wallets];
    $[13] = gateState;
    $[14] = rawWallets.isLoading;
    $[15] = rawWallets.wallets;
    $[16] = t5;
    $[17] = t6;
  } else {
    t5 = $[16];
    t6 = $[17];
  }
  useEffect(t5, t6);
  let t7;
  if ($[18] !== gateStateRef) {
    t7 = function guard(operation, surface) {
      const liveState = gateStateRef.current;
      if (canStartMultiWalletOperation(liveState)) {
        return true;
      }
      logger.warn("wallet.multi_wallet_operation_blocked", {
        operation,
        surface,
        gate_state: liveState
      });
      return false;
    };
    $[18] = gateStateRef;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const guard = t7;
  let t8;
  if ($[20] !== guard) {
    t8 = function requireOperation(operation_0, surface_0) {
      if (!guard(operation_0, surface_0)) {
        throw new MultiWalletOperationBlockedError(operation_0);
      }
    };
    $[20] = guard;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const requireOperation = t8;
  let t9;
  if ($[22] !== scope.primaryWallet?.id) {
    t9 = function canManagePrimaryById(params) {
      return scope.primaryWallet?.id === params.walletId;
    };
    $[22] = scope.primaryWallet?.id;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const canManagePrimaryById = t9;
  let t10;
  if ($[24] !== scope.primaryWallet?.address) {
    t10 = function canManagePrimaryByAddress(params_0) {
      return scope.primaryWallet?.address === params_0.address;
    };
    $[24] = scope.primaryWallet?.address;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const canManagePrimaryByAddress = t10;
  let t11;
  if ($[26] !== canManagePrimaryByAddress || $[27] !== canManagePrimaryById || $[28] !== gateStateRef || $[29] !== rawWallets || $[30] !== requireOperation || $[31] !== scope.wallets) {
    t11 = {
      ...rawWallets,
      wallets: scope.wallets,
      async createWallet(params_1) {
        requireOperation("create", "wallet_management");
        return rawWallets.createWallet(params_1);
      },
      async importWallet(params_2) {
        requireOperation("import", "wallet_management");
        return rawWallets.importWallet(params_2);
      },
      async exportWallet(params_3) {
        if (gateStateRef.current !== "on" && !canManagePrimaryByAddress(params_3)) {
          requireOperation("secondary_export", "wallet_management");
        }
        ;
        try {
          const result = await rawWallets.exportWallet(params_3);
          onEvent("wallet_export", {
            flow: "settings",
            result
          });
          return result;
        } catch (t12) {
          const err = t12;
          logger.warn("Wallet export failed", {
            error: err instanceof Error ? err.message : String(err),
            address: params_3.address
          });
          onEvent("wallet_export", {
            flow: "settings",
            result: "error"
          });
          throw err;
        }
      },
      async renameWallet(params_4) {
        if (gateStateRef.current !== "on" && !canManagePrimaryById(params_4)) {
          requireOperation("secondary_rename", "wallet_management");
        }
        return rawWallets.renameWallet(params_4);
      },
      async unlinkWallet(walletId) {
        requireOperation("external_disconnect", "wallet_management");
        return rawWallets.unlinkWallet(walletId);
      },
      async archiveWallet(walletId_0) {
        requireOperation("archive", "wallet_management");
        return rawWallets.archiveWallet(walletId_0);
      },
      async recoverWallet(walletId_1) {
        requireOperation("restore", "wallet_management");
        return rawWallets.recoverWallet(walletId_1);
      }
    };
    $[26] = canManagePrimaryByAddress;
    $[27] = canManagePrimaryById;
    $[28] = gateStateRef;
    $[29] = rawWallets;
    $[30] = requireOperation;
    $[31] = scope.wallets;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const effectiveWallets = t11;
  let t12;
  if ($[33] !== rawArchivedWallets || $[34] !== scope.archivedWallets) {
    t12 = {
      ...rawArchivedWallets,
      archivedWallets: scope.archivedWallets
    };
    $[33] = rawArchivedWallets;
    $[34] = scope.archivedWallets;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const effectiveArchivedWallets = t12;
  const t13 = !rawWallets.isLoading && (rawActiveWalletSlot.isHydrated || activeStoreHydrated);
  let t14;
  if ($[36] !== gateStateRef || $[37] !== guard || $[38] !== rawActiveWalletSlot || $[39] !== scope.activeWalletId || $[40] !== scope.primaryWallet?.id || $[41] !== t13) {
    t14 = {
      activeWalletId: scope.activeWalletId,
      isHydrated: t13,
      setActiveWalletId(walletId_2) {
        if (gateStateRef.current !== "on" && walletId_2 !== scope.primaryWallet?.id) {
          guard("switch", "indirect_transaction");
          return;
        }
        rawActiveWalletSlot.setActiveWalletId(walletId_2);
      }
    };
    $[36] = gateStateRef;
    $[37] = guard;
    $[38] = rawActiveWalletSlot;
    $[39] = scope.activeWalletId;
    $[40] = scope.primaryWallet?.id;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const effectiveActiveWalletSlot = t14;
  let t15;
  if ($[43] !== gateState || $[44] !== guard || $[45] !== scope.enabled || $[46] !== scope.primaryWallet) {
    t15 = {
      state: gateState,
      enabled: scope.enabled,
      primaryWallet: scope.primaryWallet,
      canStartOperation: guard
    };
    $[43] = gateState;
    $[44] = guard;
    $[45] = scope.enabled;
    $[46] = scope.primaryWallet;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  const featureValue = t15;
  let t16;
  if ($[48] !== rawActiveWalletSlot || $[49] !== rawArchivedWallets || $[50] !== rawWallets) {
    t16 = {
      wallets: rawWallets,
      archivedWallets: rawArchivedWallets,
      activeWalletSlot: rawActiveWalletSlot
    };
    $[48] = rawActiveWalletSlot;
    $[49] = rawArchivedWallets;
    $[50] = rawWallets;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  const rawValue = t16;
  let t17;
  if ($[52] !== children || $[53] !== effectiveActiveWalletSlot) {
    t17 = <ActiveWalletProvider value={effectiveActiveWalletSlot}>{children}</ActiveWalletProvider>;
    $[52] = children;
    $[53] = effectiveActiveWalletSlot;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] !== effectiveArchivedWallets || $[56] !== t17) {
    t18 = <ArchivedWalletsProvider value={effectiveArchivedWallets}>{t17}</ArchivedWalletsProvider>;
    $[55] = effectiveArchivedWallets;
    $[56] = t17;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] !== effectiveWallets || $[59] !== t18) {
    t19 = <WalletsProvider value={effectiveWallets}>{t18}</WalletsProvider>;
    $[58] = effectiveWallets;
    $[59] = t18;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  let t20;
  if ($[61] !== featureValue || $[62] !== t19) {
    t20 = <MultiWalletFeatureContext.Provider value={featureValue}>{t19}</MultiWalletFeatureContext.Provider>;
    $[61] = featureValue;
    $[62] = t19;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  let t21;
  if ($[64] !== rawValue || $[65] !== t20) {
    t21 = <RawWalletAccessContext.Provider value={rawValue}>{t20}</RawWalletAccessContext.Provider>;
    $[64] = rawValue;
    $[65] = t20;
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  return t21;
}
function _temp4(wallet_0) {
  return wallet_0.archivedAt === null;
}
function _temp3(wallet) {
  return wallet.type === "embedded" && wallet.isPrimary;
}
function _temp2(state_0) {
  return state_0.isActive;
}
function _temp(state) {
  return state.hasHydrated;
}
type PrimaryFailureReason = 'missing' | 'archived' | 'unavailable' | 'multiple';
function resolvePrimaryFailureReason(embeddedPrimaries: readonly SdkWallet[], primaryCandidates: readonly SdkWallet[]): PrimaryFailureReason | null {
  if (primaryCandidates.length > 1) return 'multiple';
  const primary = primaryCandidates[0];
  if (primary !== undefined) return primary.isAvailable ? null : 'unavailable';
  if (embeddedPrimaries.length === 1 && embeddedPrimaries[0]?.archivedAt !== null) return 'archived';
  return 'missing';
}